import { TrackerBatchAction } from './tracker-batch';

/**
 * Bouton « aller corriger » d'un échec du lot (demande d'Eddy du 2026-10-06) : la page inside où
 * l'échec se règle, ouverte dans un nouvel onglet pour que le récapitulatif et « Réessayer »
 * restent ouverts. Chemins des routes inside (`app.routes.ts`, `devices.routes.ts`,
 * `sites.routes.ts`, `management.routes.ts`) : aucun n'existe côté integrator.
 */
export interface BatchFailureLink {
  labelKey: string;
  path: string;
}

/** Ce que le récapitulatif sait du lot envoyé : l'action, et le site cible d'un MOVE. */
export interface BatchLinkContext {
  action: TrackerBatchAction;
  siteId?: string | null;
}

/** Le tracker en échec : son id, et le site qu'il occupait avant l'envoi (DETACH). */
export interface BatchLinkTracker {
  id: string;
  siteId?: string | null;
}

const LOGGING = new Set(['TRACKER_LOGGING_ACTIVE', 'LOGGER_SERVICE_UNAVAILABLE']);
const LORA = new Set(['LORA_SLOTS_FULL', 'LORA_PROFILE_NOT_ON_SITE', 'PROFILE_CHANGE_REQUIRES_REPROGRAMMATION']);
const ORGANIZATION = new Set(['TRACKER_HAS_NO_ORGANIZATION', 'ORGANIZATION_MISMATCH']);

/** Site concerné : la cible d'un MOVE, le site quitté d'un DETACH ; aucun pour un TRANSFER. */
function concernedSite(ctx: BatchLinkContext, tracker: BatchLinkTracker): string | null {
  if (ctx.action === 'MOVE') return ctx.siteId || null;
  if (ctx.action === 'DETACH') return tracker.siteId || null;
  return null;
}

const site = (id: string, tab: string) => `/sites/${encodeURIComponent(id)}/${tab}`;

/** Lien d'un échec, ou `null` quand il n'y a rien à ouvrir (ou pas de site connu). */
export function batchFailureLink(
  code: string,
  ctx: BatchLinkContext,
  tracker: BatchLinkTracker
): BatchFailureLink | null {
  if (LOGGING.has(code)) return { labelKey: 'trackerBatch.links.loggingSessions', path: '/devices/logging-sessions' };
  if (ORGANIZATION.has(code)) return { labelKey: 'trackerBatch.links.transferInManage', path: '/manage' };
  if (code === 'TRACKER_ON_OFFLINE_SITE') {
    return { labelKey: 'trackerBatch.links.openTracker', path: `/devices/trackers/${encodeURIComponent(tracker.id)}` };
  }
  const siteId = concernedSite(ctx, tracker);
  if (!siteId) return null;
  if (LORA.has(code)) return { labelKey: 'trackerBatch.links.openLora', path: site(siteId, 'lora') };
  if (code === 'LORA_TOPOLOGY_NOT_PUBLISHED') {
    return { labelKey: 'trackerBatch.links.applyLora', path: site(siteId, 'lora') };
  }
  if (code === 'SITE_HAS_NO_ORGANIZATION')
    return { labelKey: 'trackerBatch.links.openSite', path: site(siteId, 'info') };
  return null;
}

/**
 * Liens alignés sur `failures` : un seul bouton par (code, page), porté par le premier échec ;
 * les suivants du même groupe n'en ont pas.
 */
export function batchFailureLinks(
  failures: readonly { trackerId: string; code: string }[],
  ctx: BatchLinkContext,
  originSiteOf: (trackerId: string) => string | null | undefined
): (BatchFailureLink | null)[] {
  const seen = new Set<string>();
  return failures.map(f => {
    const link = batchFailureLink(f.code, ctx, { id: f.trackerId, siteId: originSiteOf(f.trackerId) });
    if (!link) return null;
    const key = `${f.code}|${link.path}`;
    if (seen.has(key)) return null;
    seen.add(key);
    return link;
  });
}
